package com.swetdot.swetgram.ui.picso

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.swetdot.swetgram.data.media.ImageSource
import com.swetdot.swetgram.data.media.PickedImage
import com.swetdot.swetgram.data.media.UploadPurpose
import com.swetdot.swetgram.data.media.UploadRepository
import com.swetdot.swetgram.data.newUuid
import com.swetdot.swetgram.data.picso.PicsoSource
import com.swetdot.swetgram.protocol.dto.PicsoLimits
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch

data class PicsoCaptureUiState(
    val image: PickedImage? = null,
    val caption: String = "",
    val isUploading: Boolean = false,
    val uploadProgress: Float = 0f,
    val error: String? = null,
    val isPublished: Boolean = false,
    val awaitingConfirmation: Boolean = false,
) {
    val captionRemaining: Int get() = (PicsoLimits.CAPTION_MAX - caption.length).coerceAtLeast(0)
    val canPublish: Boolean get() = image != null && !isUploading && caption.length <= PicsoLimits.CAPTION_MAX
}

class PicsoCaptureViewModel(
    private val images: () -> ImageSource? = { null },
    private val uploadRepository: UploadRepository,
    private val picsoRepository: PicsoSource,
    private val uploadPhoto: suspend (PickedImage, (Float) -> Unit) -> String = { image, progress ->
        uploadRepository.upload(image, UploadPurpose.PICSO_PHOTO, progress).id
    },
) : ViewModel() {

    private val _state = MutableStateFlow(PicsoCaptureUiState())
    val state: StateFlow<PicsoCaptureUiState> = _state.asStateFlow()
    private var uploadedId: String? = null
    private var publishKey = newUuid()
    private var picking = false

    init {
        snapPhoto()
    }

    fun snapPhoto() {
        if (picking || _state.value.isUploading || _state.value.isPublished || _state.value.awaitingConfirmation) return
        picking = true
        viewModelScope.launch {
            try {
                _state.value = _state.value.copy(error = null)
                val picker = images() ?: return@launch
                val picked = picker.pick(UploadPurpose.PICSO_PHOTO)
                if (picked != null) {
                    uploadedId = null
                    publishKey = newUuid()
                    _state.value = _state.value.copy(image = picked)
                }
            } catch (c: CancellationException) { throw c }
            catch (_: Exception) { _state.value = _state.value.copy(error = "Camera could not take a photo. Please try again.") }
            finally { picking = false }
        }
    }

    fun onCaptionChanged(newCaption: String) {
        if (!_state.value.isUploading && !_state.value.awaitingConfirmation && newCaption.length <= PicsoLimits.CAPTION_MAX) {
            if (newCaption != _state.value.caption) publishKey = newUuid()
            _state.value = _state.value.copy(caption = newCaption)
        }
    }

    fun publish() {
        val current = _state.value
        val image = current.image ?: return
        if (!current.canPublish || current.isPublished) return
        val key = publishKey
        _state.value = current.copy(isUploading = true, uploadProgress = 0.1f, error = null)

        viewModelScope.launch {
            _state.value = _state.value.copy(isUploading = true, uploadProgress = 0.1f, error = null)
            try {
                val mediaId = uploadedId ?: uploadPhoto(image) { fraction ->
                    _state.value = _state.value.copy(uploadProgress = 0.1f + fraction * 0.7f)
                }.also { uploadedId = it }
                // After a lost response this exact photo/caption/key may already be published.
                // Retrying must resolve it before the draft can be edited or replaced.
                _state.value = _state.value.copy(awaitingConfirmation = true)
                picsoRepository.create(
                    uploadId = mediaId,
                    caption = current.caption.takeIf { it.isNotBlank() },
                    clientId = key,
                )
                _state.value = _state.value.copy(isUploading = false, uploadProgress = 1f, isPublished = true)
            } catch (c: CancellationException) { throw c }
            catch (t: Exception) {
                _state.value = _state.value.copy(
                    isUploading = false,
                    error = t.message ?: "Failed to publish Picso photo",
                )
            }
        }
    }
}
